/* eslint-disable no-param-reassign */
import type { ValidationResult, ValidationRule } from '../types';
import { isValidChecksum } from './isValidChecksum';
import { stripPin } from './stripPin';

// https://gist.github.com/stpe/aebc00b9ad361d93f33d
// validate Swedish Personal Identity Number (personnummer) using checksum
// note: this is somewhat simplified because it does not take into account
// that the date of the number is valid (e.g. "000000-0000" does return as true)

const isCorrectFormat = (employeeId: string) => {
  const regExp = /^\d{8}[\s()|-]{0,1}\d{4}$/;
  return regExp.test(employeeId.toString());
};

const isValidPinDate = (pnr: string) => {
  // Given a 'YYYYMMDD-XXXX' formatted pin, capture the:
  // year part (4 digits)
  // month part (2 digits)
  // day part (2 digits)
  // and individual number part (4 digits)
  const regExp = /^(\d{4})(\d{2})(\d{2})(?:[\s()|-]?)(\d{4})$/;
  const match = pnr.match(regExp);

  if (match) {
    const [, yearPart, monthPart, dayPart, individualNumberPart] = match;
    const year = parseInt(yearPart.slice(2, 4));
    const month = parseInt(monthPart);
    const day = parseInt(dayPart);

    // Check year and month values
    if (year < 0 || year > 99 || month < 0 || month > 12) {
      return false;
    }

    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    // Adjust for leap years
    if (year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0)) {
      daysInMonth[1] = 29;
    }

    // Check that day is within reasonable range
    const dayIsValid = day > 0 && day <= daysInMonth[month - 1];

    return dayIsValid;
  } else return false;
};

export const validatePin = (pin: string) => {
  return (
    !pin.slice(-4).startsWith('000') && isCorrectFormat(pin) && isValidPinDate(pin) && isValidChecksum(stripPin(pin))
  );
};

export const isValidPinNumber =
  (errorMessage: string): ValidationRule =>
  (inputToValidate: string): ValidationResult => {
    const isValid = validatePin(inputToValidate);

    return { error: !isValid, errorMessage };
  };
